import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Trash2, ToggleLeft, ToggleRight, Link2, Eye, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui';
import type { LinkPage } from '@/types';

interface LinkPageCardProps {
  linkPage: LinkPage;
  views?: number;
  viewsLoading?: boolean;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
}

const LinkPageCard = ({ linkPage, views = 0, viewsLoading = false, onDelete, onToggleStatus }: LinkPageCardProps) => {
  const { t } = useTranslation();
  const isActive = linkPage.status === 'active';
  const publicUrl = `https://klipr.it/p/${linkPage.code}`;

  return (
    <Card variant="default" padding="md" className="hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 w-12 h-12 bg-lime-100 dark:bg-lime-900/30 rounded-xl flex items-center justify-center">
          <Link2 className="w-6 h-6 text-lime-600" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Link
            to={`/linkpages/${linkPage.id}`}
            className="text-lg font-semibold text-dark-800 dark:text-white hover:text-lime-600 transition-colors truncate block"
          >
            {linkPage.title}
          </Link>

          {linkPage.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
              {linkPage.description}
            </p>
          )}

          <div className="flex items-center gap-4 mt-2">
            <span className="text-xs text-gray-400">
              {linkPage.links?.length || 0} {t('linkPages.links')}
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {viewsLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                t('linkPages.stats.viewCount', { count: views })
              )}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
            }`}>
              {isActive ? t('linkPages.active') : t('linkPages.disabled')}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-gray-400 hover:text-lime-600 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800"
            title={t('linkPages.preview')}
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={() => onToggleStatus(linkPage.id)}
            className={`p-2 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 ${
              isActive ? 'text-green-500 hover:text-green-600' : 'text-gray-400 hover:text-gray-600'
            }`}
            title={isActive ? t('linkPages.disabled') : t('linkPages.enabled')}
          >
            {isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onDelete(linkPage.id)}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Card>
  );
};

export default LinkPageCard;
